import React, { useState, useEffect, useCallback } from "react";
import { Timestamp } from "firebase/firestore";
import { Subheading } from "../../../../design/heading";
import { Text } from "../../../../design/text";
import { Button } from "../../../../design/button";
import { Input } from "../../../../design/input";
import { Select } from "../../../../design/select";
import { Textarea } from "../../../../design/textarea";
import { Field, Label } from "../../../../design/fieldset";
import { Badge } from "../../../../design/badge";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import {
  CaseGoal,
  GoalStatus,
  GoalCategory,
  GOAL_STATUS_LABELS,
  GOAL_CATEGORY_LABELS,
} from "@visitproof/types";
import { GoalService } from "../../../../services/AssessmentService";
import { useAuth } from "../../../../contexts/AuthContext";
import { useAccount } from "../../../../contexts/AccountContext";
import { formatDateUTC, parseDateToTimestamp } from "../../../../utils/date";
import {
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
} from "@heroicons/react/20/solid";

interface Props {
  caseId: string;
}

const STATUS_BADGE_COLORS: Record<GoalStatus, "blue" | "green" | "zinc"> = {
  active: "blue",
  completed: "green",
  discontinued: "zinc",
};

const GoalCard: React.FC<{
  goal: CaseGoal;
  onStatusChange: (id: string, status: GoalStatus) => void;
  onEdit: (goal: CaseGoal) => void;
  onDelete: (id: string) => void;
}> = ({ goal, onStatusChange, onEdit, onDelete }) => (
  <div className="p-4 border rounded-lg border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Text className="font-medium text-zinc-900 dark:text-zinc-100">
            {goal.title}
          </Text>
          <Badge color={STATUS_BADGE_COLORS[goal.status]}>
            {GOAL_STATUS_LABELS[goal.status]}
          </Badge>
          {goal.category && (
            <Badge color="zinc">
              {GOAL_CATEGORY_LABELS[goal.category]}
            </Badge>
          )}
        </div>
        {goal.description && (
          <Text className="text-sm text-zinc-500 mt-1">{goal.description}</Text>
        )}
        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
          {goal.targetDate && (
            <span>Target: {formatDateUTC(goal.targetDate, "short")}</span>
          )}
          <span>Created {formatDateUTC(goal.createdAt, "short")}</span>
          {goal.createdByName && <span>by {goal.createdByName}</span>}
        </div>
        {goal.progressNotes && (
          <Text className="text-sm text-zinc-500 mt-2 italic">
            {goal.progressNotes}
          </Text>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {goal.status === "active" && (
          <>
            <Button
              plain
              onClick={() => onStatusChange(goal.id, "completed")}
              title="Mark completed"
            >
              <CheckCircleIcon className="w-4 h-4 text-green-500 hover:text-green-600" />
            </Button>
            <Button
              plain
              onClick={() => onStatusChange(goal.id, "discontinued")}
              title="Discontinue"
            >
              <XMarkIcon className="w-4 h-4 text-zinc-400 hover:text-amber-500" />
            </Button>
          </>
        )}
        {goal.status !== "active" && (
          <Button
            plain
            onClick={() => onStatusChange(goal.id, "active")}
            title="Reactivate"
          >
            <Badge color="blue" className="text-xs cursor-pointer">Reactivate</Badge>
          </Button>
        )}
        <Button plain onClick={() => onEdit(goal)} title="Edit">
          <PencilIcon className="w-4 h-4 text-zinc-400 hover:text-blue-500" />
        </Button>
        <Button plain onClick={() => onDelete(goal.id)} title="Delete">
          <TrashIcon className="w-4 h-4 text-zinc-400 hover:text-red-500" />
        </Button>
      </div>
    </div>
  </div>
);

const GoalsTab: React.FC<Props> = ({ caseId }) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [goals, setGoals] = useState<CaseGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<CaseGoal | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<GoalCategory | "">("");
  const [targetDate, setTargetDate] = useState("");
  const [progressNotes, setProgressNotes] = useState("");

  const loadGoals = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GoalService.getGoals(caseId);
      setGoals(data);
    } catch (err) {
      console.error("Error loading goals:", err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("");
    setTargetDate("");
    setProgressNotes("");
    setEditingGoal(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (goal: CaseGoal) => {
    setEditingGoal(goal);
    setTitle(goal.title);
    setDescription(goal.description ?? "");
    setCategory(goal.category ?? "");
    setTargetDate(goal.targetDate ? formatDateUTC(goal.targetDate, "input") : "");
    setProgressNotes(goal.progressNotes ?? "");
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!user || !account || !title.trim()) return;
    setSaving(true);
    try {
      if (editingGoal) {
        await GoalService.updateGoal(caseId, editingGoal.id, {
          title: title.trim(),
          description: description.trim() || undefined,
          category: category || undefined,
          targetDate: targetDate ? parseDateToTimestamp(targetDate) : undefined,
          progressNotes: progressNotes.trim() || undefined,
        });
      } else {
        await GoalService.createGoal(caseId, {
          caseId,
          accountId: account.id,
          title: title.trim(),
          description: description.trim() || undefined,
          category: category || undefined,
          status: "active",
          targetDate: targetDate ? parseDateToTimestamp(targetDate) : undefined,
          progressNotes: progressNotes.trim() || undefined,
          createdByUserId: user.uid,
          createdByName: user.displayName || user.email || "Unknown",
        });
      }
      setIsDialogOpen(false);
      resetForm();
      await loadGoals();
    } catch (err) {
      console.error("Error saving goal:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (goalId: string, status: GoalStatus) => {
    try {
      await GoalService.updateGoal(caseId, goalId, {
        status,
        resolvedDate: status !== "active" ? Timestamp.now() : undefined,
      });
      await loadGoals();
    } catch (err) {
      console.error("Error updating goal status:", err);
    }
  };

  const handleDelete = async (goalId: string) => {
    try {
      await GoalService.deleteGoal(caseId, goalId);
      await loadGoals();
    } catch (err) {
      console.error("Error deleting goal:", err);
    }
  };

  if (loading) {
    return <Text>Loading goals...</Text>;
  }

  const activeGoals = goals.filter((g) => g.status === "active");
  const resolvedGoals = goals.filter((g) => g.status !== "active");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Subheading>Goals & Outcomes</Subheading>
          <Text className="text-sm text-zinc-500">
            Track individualized goals for this case.
          </Text>
        </div>
        <Button onClick={openCreateDialog}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Goal
        </Button>
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-12">
          <Text className="text-zinc-400">No goals set for this case yet.</Text>
          <Text className="text-sm text-zinc-400 mt-1">
            Add goals to track progress and outcomes.
          </Text>
        </div>
      ) : (
        <>
          {activeGoals.length > 0 && (
            <div className="space-y-3">
              <Text className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Active Goals ({activeGoals.length})
              </Text>
              {activeGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onStatusChange={handleStatusChange}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {resolvedGoals.length > 0 && (
            <div className="space-y-3">
              <Text className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Completed / Discontinued ({resolvedGoals.length})
              </Text>
              {resolvedGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onStatusChange={handleStatusChange}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create/Edit Goal Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} size="lg">
        <DialogTitle>{editingGoal ? "Edit Goal" : "Add Goal"}</DialogTitle>
        <DialogBody className="space-y-4">
          <Field>
            <Label>Goal Title</Label>
            <Input
              type="text"
              value={title}
              placeholder="e.g., Improve parent-child communication"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Description (Optional)</Label>
            <Textarea
              rows={3}
              value={description}
              placeholder="Detailed description of the goal..."
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field>
              <Label>Category</Label>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value as GoalCategory)}
              >
                <option value="">Select...</option>
                {Object.entries(GOAL_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Target Date (Optional)</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </Field>
          </div>
          <Field>
            <Label>Progress Notes (Optional)</Label>
            <Textarea
              rows={2}
              value={progressNotes}
              placeholder="Notes about progress toward this goal..."
              onChange={(e) => setProgressNotes(e.target.value)}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button disabled={saving || !title.trim()} onClick={handleSave}>
            {saving ? "Saving..." : editingGoal ? "Update Goal" : "Add Goal"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GoalsTab;
